import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import {
  RiskAssessmentMatrixItem,
  RiskAssessmentMatrixConfig,
  RiskDimension,
  RiskLevel,
  DEFAULT_RISK_MATRIX_TEMPLATES
} from '../../../models/risk-assessment-matrix.interface';

@Component({
  selector: 'app-risk-assessment-matrix',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './risk-assessment-matrix.component.html',
  styleUrls: ['./risk-assessment-matrix.component.scss']
})
export class RiskAssessmentMatrixComponent implements OnInit {
  @Input() item!: RiskAssessmentMatrixItem;
  @Input() value: any = null;
  @Input() disabled = false;
  @Input() viewMode = false; // 預覽模式
  @Input() isFormMode = true; // 表單填寫模式，預設為 true
  @Output() valueChange = new EventEmitter<any>();

  selectedValues: number[] = [];
  calculatedResult: number = 0;
  currentRiskLevel: RiskLevel | null = null;

  ngOnInit() {
    this.initializeComponent();
    this.initializeValue();
  }

  private initializeComponent() {
    // 如果沒有配置，使用預設5級配置
    if (!this.item.config || !this.item.config.dimensions || this.item.config.dimensions.length === 0) {
      this.item.config = { ...DEFAULT_RISK_MATRIX_TEMPLATES.level5 };
    }
  }

  private initializeValue() {
    if (this.value && this.value.selectedValues) {
      this.selectedValues = [...this.value.selectedValues];
      this.calculateRisk();
    } else {
      // 初始化選擇值陣列
      this.selectedValues = new Array(this.item.config.dimensions.length).fill(0);
    }
  }

  onDimensionValueChange(dimensionIndex: number, value: number) {
    if (this.disabled) return;

    this.selectedValues[dimensionIndex] = value;
    this.calculateRisk();
    this.emitValue();
  }

  private calculateRisk() {
    if (this.selectedValues.some(v => v === 0)) {
      this.calculatedResult = 0;
      this.currentRiskLevel = null;
      return;
    }

    // 根據計算模式計算風險值
    if (this.item.config.calculationMode === 'add') {
      this.calculatedResult = this.selectedValues.reduce((sum, val) => sum + val, 0);
    } else {
      this.calculatedResult = this.selectedValues.reduce((product, val) => product * val, 1);
    }

    // 確定風險等級
    this.currentRiskLevel = this.getRiskLevelByValue(this.calculatedResult);
  }

  private getRiskLevelByValue(value: number): RiskLevel | null {
    return this.item.config.riskLevels.find(level =>
      value >= level.range[0] && value <= level.range[1]
    ) || null;
  }

  private emitValue() {
    const result = {
      selectedValues: [...this.selectedValues],
      calculatedResult: this.calculatedResult,
      riskLevel: this.currentRiskLevel?.name || ''
    };
    this.valueChange.emit(result);
  }

  // 取得矩陣所有可能的值和對應風險等級 - 用於矩陣顯示
  getMatrixCells(): Array<Array<{value: number, riskLevel: RiskLevel | null}>> {
    if (this.item.config.dimensions.length !== 2) {
      return [];
    }

    const [dim1, dim2] = this.item.config.dimensions;
    const matrix: Array<Array<{value: number, riskLevel: RiskLevel | null}>> = [];

    // 從高到低排列（y軸反轉）
    for (let i = dim1.levelCount; i >= 1; i--) {
      const row: Array<{value: number, riskLevel: RiskLevel | null}> = [];
      for (let j = 1; j <= dim2.levelCount; j++) {
        let value: number;
        if (this.item.config.calculationMode === 'add') {
          value = i + j;
        } else {
          value = i * j;
        }
        const riskLevel = this.getRiskLevelByValue(value);
        row.push({ value, riskLevel });
      }
      matrix.push(row);
    }

    return matrix;
  }

  // 檢查是否為選中的格子
  isCellSelected(rowIndex: number, colIndex: number): boolean {
    if (this.item.config.dimensions.length !== 2) return false;

    const dim1Value = this.item.config.dimensions[0].levelCount - rowIndex;
    const dim2Value = colIndex + 1;

    return this.selectedValues[0] === dim1Value && this.selectedValues[1] === dim2Value;
  }

  // 處理矩陣格子點擊
  onMatrixCellClick(rowIndex: number, colIndex: number) {
    if (this.disabled || this.viewMode) return;

    const dim1Value = this.item.config.dimensions[0].levelCount - rowIndex;
    const dim2Value = colIndex + 1;

    this.selectedValues[0] = dim1Value;
    this.selectedValues[1] = dim2Value;
    this.calculateRisk();
    this.emitValue();
  }

  // 取得維度等級名稱
  getLevelName(dimension: RiskDimension, value: number): string {
    const level = dimension.levels.find(l => l.value === value);
    return level ? level.name : value.toString();
  }

  // 取得維度等級描述
  getLevelDescription(dimension: RiskDimension, value: number): string {
    const level = dimension.levels.find(l => l.value === value);
    return level?.description || '';
  }

  // 檢查是否有有效的風險評估結果
  hasValidResult(): boolean {
    return this.calculatedResult > 0 && this.selectedValues.every(v => v > 0);
  }
}
